import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import { Caption, H6, Overline, Subtitle2 } from '@src/elements/typography/typography'
import { ExerciseResponse } from '@src/data/exercise-response'
import QrcodeVue from 'qrcode.vue'
import Exercise from '@src/data/exercise'
import { getExercisePreparationSteps } from '@src/util/functions'
import { exerciseName } from '@src/util/exercise-name'

import './exercise-card-print.sass'

const DEFAULT_PREVIEW = '/assets/icon/brand/default-exercise.jpg'
// longest wait for the picture before the print dialog opens anyway
const PRINT_TIMEOUT = 10000

@Component
export class ExerciseCardPrint extends Vue {

  @Prop({ required: true })
  exercise: ExerciseResponse | Exercise

  @Prop({ required: false, default: false })
  print: boolean

  failedPreviewUrl: string | null = null
  printed = false
  printTimer: number | null = null

  get previewSrc (): string {
    const gifUrl = this.exercise.videos.filter((video) => video.content_type === 'image/gif').map((video) => video.url)[0]
    return gifUrl && gifUrl !== this.failedPreviewUrl ? gifUrl : DEFAULT_PREVIEW
  }

  created () {
    if (this.print) {
      // the dialog waits for the picture, otherwise the card prints an empty frame
      this.printTimer = window.setTimeout(() => this.printWhenReady(), PRINT_TIMEOUT)
    }
  }

  previewLoaded () {
    this.printWhenReady()
  }

  previewFailed () {
    if (this.previewSrc !== DEFAULT_PREVIEW) {
      // the default picture's own load prints
      this.failedPreviewUrl = this.previewSrc
    } else {
      this.printWhenReady()
    }
  }

  printWhenReady () {
    if (!this.print || this.printed) {
      return
    }
    this.printed = true
    this.clearPrintTimer()
    this.printExercise()
  }

  clearPrintTimer () {
    if (this.printTimer !== null) {
      window.clearTimeout(this.printTimer)
      this.printTimer = null
    }
  }

  printFromIframes () {
    const allIframes = document.querySelectorAll('iframe')
    alert(allIframes)

    for (let i = 0; i < allIframes.length; i++) {
      let iframe = allIframes[i]
      // @ts-ignore
      iframe.sandbox += ' allow-scripts allow-modals'
    }
  }

  printExercise () {
    this.$el.classList.add('exercise-to-print')
    window.print()
  }

  beforeDestroy () {
    // a card that is gone never prints, even when its picture loads later
    this.printed = true
    this.clearPrintTimer()
    this.$el.classList.remove('exercise-to-print')
  }

  render (h: CreateElement) {
    // a card created to print stays hidden on screen while its picture loads
    return <div class={{ 'exercise-card-print': true, 'exercise-to-print': this.print }}>
      {this.renderExerciseNumber(h)}
      <div class='title-equipment'>
        {this.renderTitle(h)}
        {this.renderEquipment(h)}
      </div>
      <div class='exercise-content'>
        {this.renderPreview(h)}
        {this.renderExecutionDetails(h)}
        {this.renderQrCode(h)}
      </div>
    </div>
  }

  renderPreview (h: CreateElement) {
    return <div class='preview'>
      <img class='exercise-preview' src={this.previewSrc} onLoad={this.previewLoaded} onError={this.previewFailed} />
    </div>
  }

  renderExecutionDetails (h: CreateElement) {
    const preparationSteps: string[] = []
    getExercisePreparationSteps(this, this.exercise.number, 'DEFAULT', preparationSteps)
    if (preparationSteps.length) {
      return <div class='execution-details'>
        {preparationSteps.map((key, index) => {
          return <div class='line'>
        <span class='index'><Subtitle2>{index + 1}</Subtitle2></span>
        <span class='description'><Subtitle2>{this.$t('EXERCISE[' + this.exercise.number + '].PREPARATION_INSTRUCTIONS.' + key + '.DEFAULT')}</Subtitle2></span>
        </div>
        })}
      </div>
    }
  }

  renderTitle (h: CreateElement) {
    return <H6 class='title'>
      { exerciseName(this, this.exercise) }
    </H6>
  }

  renderExerciseNumber (h: CreateElement) {
    return <Overline class='header'>{this.exercise.number}</Overline>
  }

  renderEquipment (h: CreateElement) {
    const eqNames = this.exercise instanceof ExerciseResponse ? this.exercise.equipment : this.exercise.equipment.map(eq => eq.name ? eq.name : eq)
    return <div class='specs'>
      {this.exercise.equipment.length > 0 ?
        <Caption><b>{this.$t('REQUIRED')}</b> | {eqNames.map(eq => this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${eq.toString().toUpperCase()}`)).join(', ')}
        </Caption>
        : null}
    </div>
  }

  renderQrCode (h: CreateElement) {
    return <QrcodeVue value={`ex_${this.exercise.number}`} size={120} class='generated-qr-code' />
  }

}
